import { CreateFormControlBar } from "@/components/Common/CreateFormControlBar/CreateFormControlBar";
import type { CreateFormControlBarSummary } from "@/components/Common/CreateFormControlBar/CreateFormControlBar";
import { CreateFormStepsNav } from "@/components/Common/CreateFormStepsNav/CreateFormStepsNav";
import { ClusterFormStep } from "@/components/ClustersPage/VerticalFlowComponents/ClusterFormStep";
import { useSummaryPanelLogic } from "@/components/ClustersPage/VerticalFlowComponents/hooks/useSummaryPanelLogic";
import { usePayAndDeployAction } from "@/components/ClustersPage/VerticalFlowComponents/hooks/usePayAndDeployAction";
import GPUSelector from "@/components/ClustersPage/VerticalFlowComponents/steps/GPUSelector";
import { Loader } from "@/components/Common/Loader/Loader";
import { LightningBoltIcon } from "@/components/Icons/LightningBoltIcon";
import { Settings2Icon } from "@/components/Icons/Settings2Icon";
import { GlobeNetworkIcon } from "@/components/Icons/GlobeNetworkIcon";
import { useFormikAutoFieldName } from "@/hooks/useFormikAutoFieldName";
import { useMediaQueryMatch } from "@/hooks/useMediaQueryMatch";
import {
  ClusterType,
  DEFAULT_GPU_UNAVAILABILITY_POLICY,
  DynamicSupplyHardware,
  SimpleClusterForm
} from "@/types";
import React, { useEffect, useMemo, useRef } from "react";
import { PERSISTED_CONTAINER_CLUSTER_KEY } from "@/utils/cluster";
import { useFormikContext } from "formik";
import { useLocation } from "wouter";
import { containerVerticalFlowAnalytics } from "./analytics";
import { useContainerFormLogic, ContainerFormStep } from "./hooks/useContainerFormLogic";
import { RecipesFirstStep } from "./steps/RecipesFirstStep";
import { clearRecipeFields } from "./steps/RecipesSection.utils";
import { useContainerStore } from "@/store/simpleCluster";
import { ConfigureContainerEnvironment } from "./steps/ConfigureContainerEnvironment";
import { CustomizeContainerStep } from "./steps/CustomizeContainerStep";
import { GpusPerContainerSection } from "./steps/GpusPerContainerSection";
import { InlinePaymentSection } from "@/components/ClustersPage/CreateVirtualMachineFormV2/InlinePaymentSection";
import { QuantityDurationSection } from "@/components/ClustersPage/VerticalFlowComponents/QuantityDurationSection";
import { StandardContainer } from "@/components/Common/StandardContainer/StandardContainer";
import { Separator } from "@/components/ui/separator";
import { CreditsPaymentConfirmModal } from "@/components/ClustersPage/CreditsPaymentConfirmModal/CreditsPaymentConfirmModal";
import ENV_VARIABLES from "@/env";
import { SavedConfigsSelect } from "@/components/ClustersPage/SavedDeploymentConfigs/SavedConfigsSelect";
import {
  buildSavePayload,
  canSaveDeploymentConfig
} from "@/components/ClustersPage/SavedDeploymentConfigs/canSaveDeploymentConfig";
import { savedDeploymentPayloadsEqual } from "@/utils/savedDeploymentConfig";
import { SavedDeploymentConfig } from "@/types/savedDeploymentConfig";
import { useToast } from "@/components/ui/use-toast";
import { isPaygCluster } from "@/utils/payg";

export type CreateContainerFormV2Props = {
  formData: unknown;
  isLoading?: boolean;
  isDisabled?: boolean;
};

const CONTAINER_STEPS = [
  { id: "recipes", label: "Recipes" },
  { id: "processor", label: "Select Processor" },
  { id: "configure", label: "Configure" },
  { id: "deployment", label: "Deploy" }
];

const CONTAINER_STEP_HEADERS = [
  {
    title: "Pre-Configured Recipes",
    subtitle:
      "Pick a model, select your GPU, and launch. We handle the Docker images, environment variables, and vLLM optimization, so you don't have to."
  },
  {
    title: "Select Your Container Processor",
    subtitle: "Select the processor type optimized for your container's performance"
  },
  {
    title: "Configure",
    subtitle: "Configure container image, start command, port, health check, and environment"
  },
  {
    title: "Deploy",
    subtitle:
      "Customize your container name, set quantity and duration, and review payment details before deployment"
  }
];

export const CreateContainerFormV2: React.FC<CreateContainerFormV2Props> = ({ formData }) => {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [hasOptedOutOfRecipesFirst, setHasOptedOutOfRecipesFirst] = React.useState(false);
  const [selectedSavedConfig, setSelectedSavedConfig] =
    React.useState<SavedDeploymentConfig | null>(null);
  const isLg = useMediaQueryMatch("lg");
  // Recipes-first is the default container flow for all users.
  const isRecipesFirstEnabled = true;
  const isRecipesFirstFlagResolved = true;
  const shouldUseRecipesFirstFlow = isRecipesFirstEnabled && !hasOptedOutOfRecipesFirst;
  const { values, setValues, setFieldValue } = useFormikContext<SimpleClusterForm>();

  const fetchHardwareWithFilters = useContainerStore(
    (state) => state.fetchDynamicSupplyHardwareAndFilters
  );
  const fetchClusterHardwareInfo = useContainerStore((state) => state.fetchClusterHardwareInfo);

  const {
    loadingClusterData,
    selectedHardware,
    showSummaryPanel,
    setShowSummaryPanel,
    isPaymentDialogOpen,
    setIsPaymentDialogOpen,
    stepState,
    setStepState,
    setHasUserSpecifiedName,
    hasUserSpecifiedName,
    isSubmitDisabled,
    clusterOptions,
    computedValues,
    setCurrentStep,
    goToPreviousStep,
    onCompleteStep,
    onSelectHardware,
    onNextRecipesStep,
    onNextProcessorStep,
    currentStep,
    handleSummaryPanelAction,
    applySavedConfig
  } = useContainerFormLogic(
    formData,
    isLg,
    isRecipesFirstEnabled,
    isRecipesFirstFlagResolved,
    fetchHardwareWithFilters
  );
  const canShowProcessorStep =
    !shouldUseRecipesFirstFlow ||
    stepState.recipes === "completed" ||
    (stepState.recipes === "current" && stepState.processor === "completed");

  const handleRecipesFirstOptOut = React.useCallback(() => {
    containerVerticalFlowAnalytics.trackRecipesFirstOptOut();
    const nextValues = { ...values, containerRecipe: undefined };
    clearRecipeFields(nextValues as SimpleClusterForm);
    nextValues.trafficPort = typeof values.trafficPort === "number" ? values.trafficPort : 8000;
    setValues(nextValues);
    setHasOptedOutOfRecipesFirst(true);
    setStepState({
      recipes: "next",
      processor: "current",
      configure: "next",
      deployment: "next"
    });
  }, [setValues, values, setStepState]);

  const handleApplySavedConfig = React.useCallback(
    async (config: SavedDeploymentConfig) => {
      const hasRecipe = Boolean(config.payload.containerRecipe);
      if (hasRecipe) {
        setHasOptedOutOfRecipesFirst(false);
      }
      const { hardwareMatch } = await applySavedConfig(config, {
        onCustomContainerConfig: () => {
          setHasOptedOutOfRecipesFirst(true);
        },
        buildStepState: (hardwareMatched) => {
          if (hasRecipe) {
            // Keep recipes-first path: recipe selected → jump ahead when hardware ok
            if (hardwareMatched) {
              return {
                recipes: "completed",
                processor: "completed",
                configure: "completed",
                deployment: "current"
              };
            }
            return {
              recipes: "completed",
              processor: "current",
              configure: "next",
              deployment: "next"
            };
          }
          // Custom / opted-out path
          if (hardwareMatched) {
            return {
              recipes: "next",
              processor: "completed",
              configure: "completed",
              deployment: "current"
            };
          }
          return {
            recipes: "next",
            processor: "current",
            configure: "next",
            deployment: "next"
          };
        }
      });

      if (hardwareMatch.status === "matched") {
        setShowSummaryPanel(true);
      }

      setSelectedSavedConfig(config);

      if (hardwareMatch.status === "missing") {
        toast({
          title: "Hardware unavailable",
          description:
            "Your saved settings were loaded, but the original processor could not be found. Please select a processor.",
          variant: "error"
        });
      } else if (hardwareMatch.status === "sold_out") {
        toast({
          title: "Hardware sold out",
          description:
            "Your saved settings were loaded, but that processor is currently sold out. Please select another.",
          variant: "error"
        });
      } else {
        toast({
          title: "Configuration loaded",
          description: config.name
        });
      }
    },
    [applySavedConfig, setShowSummaryPanel, toast]
  );

  const handleBackToRecipesFromProcessor = React.useCallback(() => {
    setHasOptedOutOfRecipesFirst(false);
    setShowSummaryPanel(false);
    setStepState({
      recipes: "current",
      processor: "next",
      configure: "next",
      deployment: "next"
    });
  }, [setShowSummaryPanel, setStepState]);

  useFormikAutoFieldName({ enabled: !hasUserSpecifiedName, items: clusterOptions });

  const { durationOptions, isPriceLoading, totalCostNumber, availableQuantity } =
    useSummaryPanelLogic({
      totalCost: computedValues.totalCostNumber,
      fetchHardwareWithFilters,
      fetchClusterHardwareInfo
    });

  const isOnProcessorStep = currentStep === "processor";
  const isOnDeploymentStep = currentStep === "deployment";
  const isOnRecipesStep = shouldUseRecipesFirstFlow && currentStep === "recipes";
  const canSaveConfig = canSaveDeploymentConfig(values, "container");
  const savePayload = React.useMemo(
    () => buildSavePayload(values, "container", selectedHardware),
    [values, selectedHardware]
  );
  const canUpdateLoadedConfig = !selectedSavedConfig
    ? true
    : !savedDeploymentPayloadsEqual(savePayload, selectedSavedConfig.payload, "container");

  const {
    isPriceLoaded,
    totalCost,
    paymentLink,
    isSubmitting,
    showSavingsMessage,
    totalSavingsAmountFormatted,
    requestPayAndDeploy,
    isCreditsConfirmOpen,
    creditsTopUpPaymentMethod,
    setCreditsTopUpPaymentMethod,
    confirmCreditsPayment,
    cancelCreditsPaymentConfirm,
    cancelPayment,
    mockSpherePayment,
    purchasedCreditsGate,
    minNonPromotionalCreditsRequired
  } = usePayAndDeployAction({
    isActive: isOnDeploymentStep,
    initialTotalCost: Number(totalCostNumber || 0),
    analytics: containerVerticalFlowAnalytics,
    persistedStorageKey: PERSISTED_CONTAINER_CLUSTER_KEY,
    fetchClusterHardwareInfo
  });

  // Intercept the dialog-open signal and run payment inline
  useEffect(() => {
    if (isPaymentDialogOpen) {
      setIsPaymentDialogOpen(false);
      requestPayAndDeploy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaymentDialogOpen]);

  // Resolve the currently selected hardware (falling back to the persisted list
  // when `selectedHardware` is not yet populated, e.g. after a page refresh).
  const resolvedHardware = useMemo((): DynamicSupplyHardware | null => {
    if (selectedHardware) return selectedHardware;
    if (!values?.hardware) return null;
    const list =
      ((values?.createClusterData as unknown as { hardwareList?: DynamicSupplyHardware[] })
        ?.hardwareList as DynamicSupplyHardware[]) || [];
    return list.find((item) => item.hardwareId === values.hardware) ?? null;
  }, [selectedHardware, values?.hardware, values?.createClusterData]);

  // "Number of GPUs per Container" is only configurable for external supply,
  // where a single node can host more GPUs than a container needs.
  const isExternalSupplier = (resolvedHardware?.supplier ?? values.supplier) === "external";
  const maxGpusPerContainer = Number(resolvedHardware?.numCards ?? 1) || 1;
  const showReplicaQuantity =
    !isExternalSupplier && ENV_VARIABLES.FEATUREFLAG_CONTAINER_REPLICA_COUNT_ENABLED;
  const showQuantityDurationSection =
    !isPaygCluster(values.clusterType) || (showReplicaQuantity && availableQuantity.length > 0);

  // Keep `gpusPerContainer` in sync with the selected hardware: default it to the
  // hardware's GPU count (num_cards), reset it whenever the hardware changes, and
  // clamp any out-of-range value. The user can only lower it (for external supply).
  // Empty / partial values are left alone so QuantityField mid-edit (backspace → retype)
  // isn't immediately overwritten back to the max.
  const lastSeededHardwareRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!resolvedHardware) return;
    const current = values.gpusPerContainer;
    const hardwareChanged = lastSeededHardwareRef.current !== values.hardware;
    const isEmpty = current === "" || current === undefined || current === null;
    const parsed = typeof current === "number" ? current : parseInt(String(current ?? ""), 10);
    const hasNumericValue = !isNaN(parsed);

    if (hardwareChanged) {
      setFieldValue("gpusPerContainer", maxGpusPerContainer);
    } else if (hasNumericValue && (parsed < 1 || parsed > maxGpusPerContainer)) {
      setFieldValue("gpusPerContainer", Math.min(maxGpusPerContainer, Math.max(1, parsed)));
    } else if (!hasNumericValue && !isEmpty) {
      // Non-numeric garbage (not mid-edit empty) — fall back to max
      setFieldValue("gpusPerContainer", maxGpusPerContainer);
    }

    if (!values.gpuUnavailabilityPolicy) {
      setFieldValue("gpuUnavailabilityPolicy", DEFAULT_GPU_UNAVAILABILITY_POLICY);
    }
    lastSeededHardwareRef.current = values.hardware;
  }, [
    resolvedHardware,
    values.hardware,
    values.gpusPerContainer,
    values.gpuUnavailabilityPolicy,
    maxGpusPerContainer,
    setFieldValue
  ]);

  // Derive hardware summary for the control bar footer
  const controlBarSummary = useMemo((): CreateFormControlBarSummary | undefined => {
    const hw = resolvedHardware;

    if (!hw) return undefined;

    const pricePerHr =
      typeof hw.price === "number" && hw.price > 0
        ? `$${hw.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}/hr`
        : "-";

    return {
      processorLabel: hw.hardwareName,
      gpusPerHardware: hw.numCards,
      hourlyPrice: pricePerHr,
      vramPerCard: hw.vramPerCard,
      supplier: hw.supplier ?? (values.supplier as "internal" | "external" | undefined),
      locationCode: hw.location,
      locationName: hw.location
    };
  }, [resolvedHardware, values?.supplier]);

  const navStepIndex = CONTAINER_STEPS.findIndex((s) => s.id === currentStep);

  const isOptedOutOnProcessorStep =
    isOnProcessorStep && isRecipesFirstEnabled && hasOptedOutOfRecipesFirst;
  const isRecipesFlowOnProcessorStep = isOnProcessorStep && isRecipesFirstEnabled;
  const controlBarBackLabel =
    isOnProcessorStep && !isRecipesFlowOnProcessorStep ? "Back to Containers" : "Back";
  const controlBarContinueLabel = isOnDeploymentStep
    ? isSubmitting
      ? "Confirming Payment…"
      : "Pay & Deploy"
    : "Continue";

  const handleControlBarBack = () => {
    if (isOptedOutOnProcessorStep) {
      handleBackToRecipesFromProcessor();
    } else if (isRecipesFlowOnProcessorStep) {
      goToPreviousStep(currentStep);
    } else if (isOnProcessorStep || isOnRecipesStep) {
      navigate("/cloud/container");
    } else {
      goToPreviousStep(currentStep);
    }
  };

  const handleControlBarContinue = () => {
    if (isOnRecipesStep) {
      if (!values.containerRecipe) return;
      onNextRecipesStep();
      return;
    }
    if (isOnProcessorStep) {
      if (!resolvedHardware) return;
      onNextProcessorStep(resolvedHardware);
      return;
    }
    if (isOnDeploymentStep || currentStep === "configure") {
      handleSummaryPanelAction();
      return;
    }
    onCompleteStep(currentStep as ContainerFormStep);
  };

  const isContinueDisabled = isOnRecipesStep
    ? !values.containerRecipe
    : isOnProcessorStep
      ? !resolvedHardware
      : isOnDeploymentStep
        ? isSubmitDisabled || isSubmitting
        : isSubmitDisabled;

  // suppress unused warnings for values from useSummaryPanelLogic (used indirectly)
  void isPriceLoading;

  return (
    <form className="flex flex-1 flex-col border-t border-border">
      {/* Top step navigation — shared with VM form */}
      <CreateFormStepsNav
        steps={CONTAINER_STEPS}
        currentStepIndex={Math.max(0, navStepIndex)}
        onStepClick={(index) => {
          const stepId = CONTAINER_STEPS[index]?.id as ContainerFormStep | undefined;
          if (stepId && index < navStepIndex) {
            if (stepId === "recipes") {
              setHasOptedOutOfRecipesFirst(false);
            }
            setCurrentStep(stepId);
          }
        }}
        title={navStepIndex >= 0 ? CONTAINER_STEP_HEADERS[navStepIndex].title : undefined}
        description={navStepIndex >= 0 ? CONTAINER_STEP_HEADERS[navStepIndex].subtitle : undefined}
        savedInConfig={navStepIndex === 0 || navStepIndex === 1 || navStepIndex === 2}
        actions={
          <SavedConfigsSelect
            kind="container"
            selectedId={selectedSavedConfig?.id}
            selectedConfig={selectedSavedConfig}
            onSelect={handleApplySavedConfig}
            savePayload={savePayload}
            canSave={canSaveConfig}
            canUpdateLoaded={canUpdateLoadedConfig}
            onSaved={setSelectedSavedConfig}
          />
        }
      />

      {/* Scrollable content area */}
      <div className="flex flex-1 flex-col gap-4 pb-6 pt-6">
        {loadingClusterData ? (
          <div className="flex h-[calc(100vh-216px)] items-center justify-center">
            <Loader color="#ffffff" className="h-[40px] w-[40px]" />
          </div>
        ) : (
          <>
            {/* ── Step 1: Recipes ── */}
            {shouldUseRecipesFirstFlow && stepState.recipes !== "completed" && (
              <RecipesFirstStep
                onSelectGpu={handleRecipesFirstOptOut}
                onAdvanceRecipesStep={onNextRecipesStep}
              />
            )}
            {shouldUseRecipesFirstFlow && stepState.recipes === "completed" && (
              <ClusterFormStep
                id="recipes"
                icon={<Settings2Icon className="text-foreground max-lg:h-4 max-lg:w-4" />}
                title=""
                component={<></>}
                // completedTitle="Selected Recipe"
                // completedComponent={<RecipesStepCompletedCard />}
                state="completed"
                onEdit={() => setCurrentStep("recipes")}
              />
            )}

            {/* ── Step 2: Select Processor ── */}
            {canShowProcessorStep && (
              <ClusterFormStep
                id="processor"
                icon={<LightningBoltIcon className="text-foreground max-lg:h-4 max-lg:w-4" />}
                title=""
                component={
                  <StandardContainer>
                    <GPUSelector
                      fetchHardwareWithFilters={fetchHardwareWithFilters}
                      onSelectHardware={(hardware) => {
                        onSelectHardware(hardware);
                        if (!isLg) setShowSummaryPanel(true);
                      }}
                      onAdvanceProcessorStep={(hw) => {
                        onSelectHardware(hw);
                        if (!isLg) {
                          setShowSummaryPanel(true);
                        }
                        onNextProcessorStep(hw);
                      }}
                      clusterType={ClusterType.CONTAINER}
                      showSummaryPanel={showSummaryPanel}
                      analytics={containerVerticalFlowAnalytics}
                    />
                  </StandardContainer>
                }
                // completedTitle="Chosen GPU"
                // completedComponent={
                //   <GPUStepCompletedCard
                //     processor={computedValues.processorLabel}
                //     numCards={Number(computedValues.gpusPerHardware)}
                //     price={Number(computedValues.priceNumber)}
                //     location={computedValues.locationLabel}
                //     locationId={computedValues.locationId || "-"}
                //   />
                // }
                state={stepState.processor}
                isNextDisabled={!selectedHardware}
                onEdit={() => setCurrentStep("processor")}
              />
            )}

            {showSummaryPanel && (
              <>
                {/* ── Step 3: Configure — hidden until reached ── */}
                {stepState.configure !== "next" && (
                  <ClusterFormStep
                    id="configure"
                    icon={<Settings2Icon className="text-foreground max-lg:h-4 max-lg:w-4" />}
                    title=""
                    component={
                      <>
                        {/* <div className="w-full border-t border-border pt-4">
                        <ConfigureContainerEnvironment
                          skippedRecipesFirst={hasOptedOutOfRecipesFirst}
                        />
                      </div> */}
                        <ConfigureContainerEnvironment
                          skippedRecipesFirst={hasOptedOutOfRecipesFirst}
                          showRecipes={!shouldUseRecipesFirstFlow}
                        />
                      </>
                    }
                    // completedComponent={<></>}
                    state={stepState.configure}
                    onEdit={() => setCurrentStep("configure")}
                  />
                )}

                {/* ── Step 4: Deploy — hidden until reached ── */}
                {stepState.deployment !== "next" && (
                  <ClusterFormStep
                    id="deployment"
                    icon={<GlobeNetworkIcon className="text-foreground max-lg:h-4 max-lg:w-4" />}
                    title=""
                    component={
                      <div className="flex flex-col gap-4">
                        <CustomizeContainerStep
                          onNameChange={() => setHasUserSpecifiedName(true)}
                        />

                        {isExternalSupplier && (
                          <>
                            <Separator className="my-3" />
                            <StandardContainer>
                              <GpusPerContainerSection
                                maxGpus={maxGpusPerContainer}
                                availableQuantity={availableQuantity}
                                showReplicas={
                                  ENV_VARIABLES.FEATUREFLAG_CONTAINER_REPLICA_COUNT_ENABLED
                                }
                              />
                            </StandardContainer>
                          </>
                        )}

                        {showQuantityDurationSection && (
                          <>
                            <Separator className="my-3" />
                            <StandardContainer>
                              <QuantityDurationSection
                                durationOptions={durationOptions}
                                availableQuantity={availableQuantity}
                                showQuantityStepper={showReplicaQuantity}
                                className="pt-0"
                              />
                            </StandardContainer>
                          </>
                        )}
                        <InlinePaymentSection
                          isPriceLoaded={isPriceLoaded && !isPriceLoading}
                          totalCost={totalCost}
                          showSavingsMessage={showSavingsMessage}
                          totalSavingsAmountFormatted={totalSavingsAmountFormatted}
                          paymentLink={paymentLink}
                          onMockPayment={
                            paymentLink ? () => mockSpherePayment(paymentLink) : undefined
                          }
                          purchasedCreditsGate={purchasedCreditsGate}
                          minNonPromotionalCreditsRequired={minNonPromotionalCreditsRequired}
                        />
                      </div>
                    }
                    completedComponent={<></>}
                    state={stepState.deployment}
                    onEdit={() => setCurrentStep("deployment")}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Bottom control bar — shared with VM form */}
      <CreateFormControlBar
        backLabel={controlBarBackLabel}
        onBack={handleControlBarBack}
        continueLabel={controlBarContinueLabel}
        onContinue={handleControlBarContinue}
        isContinueDisabled={isContinueDisabled}
        isPaymentPending={isSubmitting && isOnDeploymentStep}
        onConfirmCancelPayment={cancelPayment}
        summary={controlBarSummary}
        summaryDetailField="supplier"
      />

      <CreditsPaymentConfirmModal
        isOpen={isCreditsConfirmOpen}
        amount={Number(totalCost || totalCostNumber || 0)}
        minNonPromotionalCreditsRequired={minNonPromotionalCreditsRequired}
        isPayg={isPaygCluster(values.clusterType)}
        isLoading={isSubmitting}
        topUpPaymentMethod={creditsTopUpPaymentMethod}
        onTopUpPaymentMethodChange={setCreditsTopUpPaymentMethod}
        onConfirm={confirmCreditsPayment}
        onClose={cancelCreditsPaymentConfirm}
      />
    </form>
  );
};
